import Big from 'big.js';
import NumberFormat from 'react-number-format';
import { getIntl } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';

type Props = {
    amount: number | string;
    currency?: string;
    displaySign?: boolean;
    decimalScale?: number;
    negate?: boolean;
};

/**
 * An amount as the new design's locale writes it, its ISO code where the locale puts one: "EUR 1,234.50". `displaySign`
 * signs a gain with "+" as a loss is signed; a zero is never signed.
 */
export const formatAmount = (
    value: number,
    currency = '',
    { decimalScale = 2, displaySign = false }: { decimalScale?: number; displaySign?: boolean } = {}
): string =>
    // The locale rounds, half away from zero on the decimal the value prints as; one it rounds to zero is written as an
    // unsigned zero, which `signDisplay: 'negative'` would do but older Firefox lacks.
    getIntl().formatNumber(Math.abs(value) < 0.5 / 10 ** decimalScale ? 0 : value, {
        ...(currency ? ({ style: 'currency', currency, currencyDisplay: 'code' } as const) : {}),
        minimumFractionDigits: decimalScale,
        maximumFractionDigits: decimalScale,
        signDisplay: displaySign ? 'exceptZero' : 'auto',
    });

/** A field's amount written as {@link formatAmount} writes it; anything that is not yet a number stays as typed. */
export const formatAmountValue = (value: string): string =>
    value.trim() === '' || isNaN(Number(value)) ? value : formatAmount(Number(value));

const MoneyDisplay = ({
    amount,
    currency = '',
    displaySign = false,
    decimalScale = 2,
    negate = false,
}: Props): JSX.Element => {
    try {
        const bigAmount = new Big(amount);
        const originalValue = Math.round(Number(amount) * 100) / 100;
        const value = negate ? originalValue * -1 : originalValue;
        if (isNewDesignCopy()) {
            return (
                <span className="whitespace-nowrap tabular-nums">
                    {formatAmount(value, currency, { decimalScale, displaySign })}
                </span>
            );
        }
        return (
            <NumberFormat
                displayType="text"
                fixedDecimalScale={true}
                decimalScale={decimalScale}
                prefix={bigAmount.gt(0) && displaySign ? '+' : undefined}
                suffix={currency?.length > 0 ? ` ${currency}` : undefined}
                value={value}
                thousandSeparator={' '}
                className="tabular-nums"
                style={{ whiteSpace: 'nowrap' }}
            />
        );
    } catch (e) {
        console.error(e);
        return (
            <NumberFormat
                displayType="text"
                fixedDecimalScale={true}
                decimalScale={decimalScale}
                suffix={currency?.length > 0 ? ` ${currency}` : undefined}
                value={-99999}
                className="tabular-nums"
            />
        );
    }
};
export default MoneyDisplay;
